import dayjs from "dayjs";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { User, Hotel, ArrowDownLeft, ArrowUpRight, RefreshCw, CheckCircle2 } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback, TableRow, TableCell } from "@hotelise/ui";

import { ReportTableItemWithTransfer } from "interfaces/reports";

import { getChannelName } from "utils/channels";
import { cn } from "utils/cn";

import { MAIN_THEME } from "styles/colors";

const money = (n: number, currency: string) =>
	n === 0 ? `${currency} 0` : new Intl.NumberFormat(undefined, { style: "currency", currency }).format(n);


interface Props {
	data: ReportTableItemWithTransfer;
	showProperty?: boolean;
	onSelect: (item: ReportTableItemWithTransfer | null) => void;
	onCleaningClick?: (item: ReportTableItemWithTransfer) => void;
	/** Pre-signed S3 URL for the first cleaning thumbnail — signed at list level for efficiency */
	signedThumbnailUrl?: string;
	visibleColumnIds?: string[];
	viewMode?: "compact" | "financial";
}

const PropertyReportDataItem = (props: Props): JSX.Element => {
	const { data, showProperty, onSelect, onCleaningClick, signedThumbnailUrl, visibleColumnIds, viewMode } = props;
	const {
		start_date,
		end_date,
		guest,
		currency,
		accommodation,
		host_service_fee,
		cleaning,
		payout_price,
		net_accomdation,
		hotelise_help,
		owner_income,
		channel,
		code,
		status,
		adults,
	} = data._item;
	const { _accountHolder, _toTransfer } = data._transferDetails;

	const channelName = useMemo(() => {
		const name = getChannelName(channel).toLowerCase();
		if (name === "airbnb-official") return "airbnb";
		return name;
	}, [channel]);

	const renderAccountHolderIcon = () =>
		_accountHolder === 1 ? (
			<Hotel className="h-[18px] w-[18px] text-primary" />
		) : (
			<User className="h-5 w-5 text-primary" />
		);

	const name = useMemo(() => {
		const nameWithoutDash = (data.property?.name || "").replaceAll("-", " ");
		const nameWithoutUnderscore = nameWithoutDash.replaceAll("_", " ");

		return (
			nameWithoutUnderscore.charAt(0).toUpperCase() +
			nameWithoutUnderscore.slice(1)
		);
	}, [data]);

	const renderStatus = () => {
		const lowerStatus = (status || "n/a").toLowerCase();
		const isAccepted = lowerStatus === "accepted";

		return (
			<TableCell>
				<div
					className="px-1.5 py-px rounded-full text-[10px] font-semibold lowercase tracking-wide inline-flex items-center justify-center border"
					style={{
						background: isAccepted ? "rgba(55, 203, 123, 0.15)" : `${MAIN_THEME.CANCEL_COLOR}25`,
						color: isAccepted ? "#37CB7B" : MAIN_THEME.CANCEL_COLOR,
						borderColor: isAccepted ? "rgba(55, 203, 123, 0.25)" : `${MAIN_THEME.CANCEL_COLOR}30`,
					}}
				>
					{lowerStatus}
				</div>
			</TableCell>
		);
	};

	const renderChannel = () => {
		return (
			<TableCell>
				<div
					className="px-1.5 py-px rounded-full text-[10px] font-semibold lowercase tracking-wide inline-flex items-center justify-center border"
					style={{
						background: "rgba(148, 163, 184, 0.15)",
						color: "hsl(var(--foreground))",
						borderColor: "rgba(148, 163, 184, 0.25)",
					}}
				>
					{channelName}
				</div>
			</TableCell>
		);
	};

	const isVisible = (id: string) => {
		if (!visibleColumnIds) return true;
		return visibleColumnIds.includes(id);
	}

	return (
		<>
			<TableRow
				onClick={() => onSelect(data)}
				className="cursor-pointer hover:bg-muted/50 transition-colors duration-200"
			>
				{showProperty && isVisible('property') ? (
					<TableCell>
						<Link
							to={`/properties/${data.property?.id || ''}`}
							className="flex items-center gap-2 hover:underline"
							onClick={(e) => e.stopPropagation()}
						>
							<Avatar className="h-8 w-8 rounded-md shrink-0">
								<AvatarImage
									src={data.property?.picture || ''}
									alt={name}
									className="object-cover"
								/>
								<AvatarFallback className="rounded-md text-[10px]">
									{(name || '').slice(0, 2).toUpperCase()}
								</AvatarFallback>
							</Avatar>
							<span className="text-sm font-medium truncate">{name}</span>
						</Link>
					</TableCell>
				) : null}
				{isVisible('channel') && renderChannel()}
				{isVisible('code') && <TableCell>{code}</TableCell>}

				{isVisible('status') && renderStatus()}
				{isVisible('guests') && (
					<TableCell>
						<div className="leading-tight">
							<div className="font-medium text-primary">{`${guest.first_name} ${guest.last_name}`}</div>
							<div className="text-xs text-muted-foreground">{`${adults || 0} adults`}</div>
						</div>
					</TableCell>
				)}
				{isVisible('checkin') && <TableCell>{dayjs(start_date).format("D MMM, YYYY")}</TableCell>}
				{isVisible('checkout') && <TableCell>{dayjs(end_date).format("D MMM, YYYY")}</TableCell>}
				{isVisible('cleaningPhotos') && (
					<TableCell className="whitespace-nowrap">
						{data._item.cleaningData ? (
							(() => {
								const cd = data._item.cleaningData!;
								const imageCount = cd.images?.length ?? 0;
								const statusIcon = cd.status === 'started' ? (
									<RefreshCw className="h-3.5 w-3.5 text-amber-500 animate-spin shrink-0" />
								) : (
									<CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
								);
								if (imageCount > 0 && signedThumbnailUrl) {
									return (
										<button
											type="button"
											className="flex items-center gap-1.5 group"
											onClick={(e) => { e.stopPropagation(); onCleaningClick?.(data); }}
										>
											<div className="relative h-8 w-8 shrink-0 rounded-md overflow-hidden">
												<img src={signedThumbnailUrl} alt="Cleaning" className="h-full w-full object-cover" />
												{imageCount > 1 && (
													<div className="absolute inset-0 bg-black/50 flex items-center justify-center">
														<span className="text-[10px] font-semibold text-white">+{imageCount - 1}</span>
													</div>
												)}
											</div>
											{statusIcon}
										</button>
									);
								}
								return <span className="flex items-center gap-1">{statusIcon}</span>;
							})()
						) : (
							<span className="text-xs text-muted-foreground">—</span>
						)}
					</TableCell>
				)}

				{isVisible('ownerIncome') && <TableCell>{currency} {owner_income}</TableCell>}
				{isVisible('sender') && <TableCell>{renderAccountHolderIcon()}</TableCell>}
			</TableRow>
			{viewMode === "financial" && (
				<TableRow className="bg-muted/30 hover:bg-muted/40">
					<TableCell colSpan={visibleColumnIds?.length || 9} className="py-2 px-4">
						<div className="flex items-center gap-6 text-xs tabular-nums">
							<div className="flex items-center gap-1.5">
								<span className="text-muted-foreground">Accom.</span>
								<span className="font-medium">{money(accommodation, currency)}</span>
							</div>
							<div className="flex items-center gap-1.5">
								<span className="text-muted-foreground">Ch. Fee</span>
								<span className="font-medium">{money(host_service_fee, currency)}</span>
							</div>
							<div className="flex items-center gap-1.5">
								<span className="text-muted-foreground">Net Acc.</span>
								<span className="font-medium">{money(net_accomdation, currency)}</span>
							</div>
							<div className="flex items-center gap-1.5">
								<span className="text-muted-foreground">Cleaning</span>
								<span className="font-medium">{money(cleaning, currency)}</span>
							</div>
							<div className="flex items-center gap-1.5">
								<span className="text-muted-foreground">Payout</span>
								<span className="font-medium">{money(payout_price, currency)}</span>
							</div>
							<div className="flex items-center gap-1.5">
								<span className="text-muted-foreground">Hotelise</span>
								<span className="font-medium">{money(hotelise_help, currency)}</span>
							</div>
							<div className="h-3 w-px bg-border" />
							<div className="flex items-center gap-1.5">
								<span className="text-foreground font-semibold">Transfer</span>
								{_accountHolder === 1 ? (
									<ArrowDownLeft className="h-3 w-3 text-emerald-500 shrink-0" />
								) : (
									<ArrowUpRight className="h-3 w-3 text-amber-500 shrink-0" />
								)}
								<span className={cn(
									"font-semibold",
									_accountHolder === 1
										? "text-emerald-600 dark:text-emerald-400"
										: "text-amber-600 dark:text-amber-400"
								)}>
									{money(_toTransfer, currency)}
								</span>
							</div>
						</div>
					</TableCell>
				</TableRow>
			)}
		</>
	);
};

export default PropertyReportDataItem;
